class PolariveTheme {
  constructor() {
    this.revealObserver = null;
    this.init(document);
    this.bindThemeEditor();
  }

  init(root = document) {
    this.bindReveals(root);
    this.bindTabs();
  }

  bindReveals(root) {
    const elements = root.querySelectorAll('.polarive-reveal:not([data-polarive-reveal-ready])');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    this.revealObserver ||= new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          this.revealObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.12 }
    );

    elements.forEach((element) => {
      element.dataset.polariveRevealReady = 'true';
      this.revealObserver.observe(element);
    });
  }

  getGroupedSections(navigation, group) {
    const navigationSection = navigation.closest('.shopify-section');
    if (!navigationSection) return [];

    document
      .querySelectorAll(`[data-polarive-tab-assigned-group="${CSS.escape(group)}"]`)
      .forEach((section) => {
        delete section.dataset.polariveTabAssignedGroup;
        delete section.dataset.polariveTabAssignedTarget;
        section.hidden = false;
        section.removeAttribute('aria-hidden');
      });

    const grouped = [];
    let currentTarget = '';
    let sibling = navigationSection.nextElementSibling;

    while (sibling) {
      const boundary = sibling.querySelector(`[data-polarive-tab-boundary][data-polarive-tab-group="${CSS.escape(group)}"]`);

      if (boundary?.dataset.polariveTabBoundary === 'end') {
        sibling.hidden = true;
        sibling.dataset.polariveTabBoundaryWrapper = 'true';
        break;
      }

      if (boundary?.dataset.polariveTabBoundary === 'start') {
        currentTarget = boundary.dataset.polariveTabTarget;
        sibling.hidden = true;
        sibling.dataset.polariveTabBoundaryWrapper = 'true';
        sibling.dataset.polariveTabAssignedTarget = currentTarget;
      } else if (currentTarget) {
        sibling.dataset.polariveTabAssignedGroup = group;
        sibling.dataset.polariveTabAssignedTarget = currentTarget;
        grouped.push(sibling);
      }

      sibling = sibling.nextElementSibling;
    }

    return grouped;
  }

  bindTabs() {
    document.querySelectorAll('[data-polarive-tabs]').forEach((navigation) => {
      const group = navigation.dataset.polariveTabGroup;
      const buttons = [...navigation.querySelectorAll('[data-polarive-tab-target]')];
      if (!group || !buttons.length) return;

      const activate = (target, updateHash = false) => {
        const groupedSections = this.getGroupedSections(navigation, group);
        const directPanels = [
          ...document.querySelectorAll(
            `[data-polarive-tab-panel][data-polarive-tab-group="${CSS.escape(group)}"]`
          ),
        ];
        const availableTargets = new Set([
          ...groupedSections.map((section) => section.dataset.polariveTabAssignedTarget),
          ...directPanels.map((panel) => panel.dataset.polariveTabPanel),
        ]);
        if (!availableTargets.has(target)) return;

        buttons.forEach((button) => {
          const active = button.dataset.polariveTabTarget === target;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-selected', String(active));
          button.tabIndex = active ? 0 : -1;
        });

        groupedSections.forEach((section) => {
          const active = section.dataset.polariveTabAssignedTarget === target;
          section.hidden = !active;
          section.setAttribute('aria-hidden', String(!active));
        });

        directPanels.forEach((panel) => {
          const wrapper = panel.closest('.shopify-section');
          if (wrapper?.dataset.polariveTabAssignedGroup === group) return;
          const active = panel.dataset.polariveTabPanel === target;
          panel.hidden = !active;
          panel.setAttribute('aria-hidden', String(!active));
        });

        if (updateHash && history.replaceState) {
          history.replaceState(null, '', `#${target}`);
        }
      };

      navigation.polariveActivateTab = activate;

      if (navigation.dataset.polariveTabsReady !== 'true') {
        navigation.dataset.polariveTabsReady = 'true';

        buttons.forEach((button, index) => {
          button.addEventListener('click', () => activate(button.dataset.polariveTabTarget, true));
          button.addEventListener('keydown', (event) => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            let next = index;
            if (event.key === 'ArrowRight') next = (index + 1) % buttons.length;
            if (event.key === 'ArrowLeft') next = (index - 1 + buttons.length) % buttons.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = buttons.length - 1;
            buttons[next].focus();
            activate(buttons[next].dataset.polariveTabTarget, true);
          });
        });
      }

      const selected = buttons.find((button) => button.classList.contains('is-active'));
      const hashTarget = window.location.hash.replace('#', '');
      const initial = buttons.some((button) => button.dataset.polariveTabTarget === hashTarget)
        ? hashTarget
        : selected?.dataset.polariveTabTarget || buttons[0].dataset.polariveTabTarget;
      activate(initial);
    });
  }

  activateSection(section) {
    const boundary = section.querySelector?.('[data-polarive-tab-boundary]');
    const directPanel = section.querySelector?.('[data-polarive-tab-panel]');
    const group =
      section.dataset.polariveTabAssignedGroup ||
      boundary?.dataset.polariveTabGroup ||
      directPanel?.dataset.polariveTabGroup;
    const target =
      section.dataset.polariveTabAssignedTarget ||
      boundary?.dataset.polariveTabTarget ||
      directPanel?.dataset.polariveTabPanel;
    if (!group || !target) return;

    const navigation = document.querySelector(
      `[data-polarive-tabs][data-polarive-tab-group="${CSS.escape(group)}"]`
    );
    navigation?.polariveActivateTab?.(target);
  }

  bindThemeEditor() {
    document.addEventListener('shopify:section:load', () => this.bindTabs());
    document.addEventListener('shopify:section:reorder', () => this.bindTabs());
    document.addEventListener('shopify:section:select', (event) => this.activateSection(event.target));
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new PolariveTheme());
} else {
  new PolariveTheme();
}
